import { useRef } from 'react';
import { useEditor, UPDATE_PROPERTY, SELECT_LAYER, snapToFrame, useComposition } from '@/lib/kinema';
import type { BaseLayerData } from '@/lib/kinema';
import type { TimelineScale } from './useTimelineScale';

interface LayerTrackProps {
  layer: BaseLayerData;
  scale: TimelineScale;
  isSelected: boolean;
}

const TYPE_COLORS: Record<string, string> = {
  shape: '#3b82f6',
  text: '#8b5cf6',
  image: '#10b981',
  video: '#f59e0b',
  audio: '#ef4444',
  composition: '#6366f1',
};

const HANDLE_WIDTH = 6;

export function LayerTrack({ layer, scale, isSelected }: LayerTrackProps) {
  const editor = useEditor();
  const comp = useComposition();
  const { timeToX, xToTime } = scale;
  const barRef = useRef<HTMLDivElement>(null);

  const left = timeToX(layer.startTime);
  const width = timeToX(layer.endTime - layer.startTime);
  const color = TYPE_COLORS[layer.type] ?? '#64748b';

  const selectLayer = (e?: React.MouseEvent | MouseEvent) => {
    editor.dispatchCommand(SELECT_LAYER, { layerId: layer.id, addToSelection: e?.metaKey || e?.ctrlKey });
  };

  // Drag the whole bar — commit only on drop
  const handleBarDrag = (e: React.MouseEvent) => {
    e.stopPropagation();
    selectLayer(e);
    const startX = e.clientX;
    const origStart = layer.startTime;
    const origEnd = layer.endTime;
    const duration = origEnd - origStart;

    const handleMove = (moveEvent: MouseEvent) => {
      const dx = moveEvent.clientX - startX;
      const dt = xToTime(dx);
      const newStart = snapToFrame(origStart + dt, comp.frameRate);
      if (barRef.current) {
        barRef.current.style.left = `${timeToX(newStart)}px`;
      }
    };
    const handleUp = (upEvent: MouseEvent) => {
      window.removeEventListener('mousemove', handleMove);
      window.removeEventListener('mouseup', handleUp);
      const dx = upEvent.clientX - startX;
      const dt = xToTime(dx);
      const newStart = snapToFrame(origStart + dt, comp.frameRate);
      if (newStart !== origStart) {
        editor.batch(() => {
          editor.dispatchCommand(UPDATE_PROPERTY, { layerId: layer.id, property: 'startTime', value: newStart });
          editor.dispatchCommand(UPDATE_PROPERTY, { layerId: layer.id, property: 'endTime', value: newStart + duration });
        });
      }
    };
    window.addEventListener('mousemove', handleMove);
    window.addEventListener('mouseup', handleUp);
  };

  // Left handle — commit only on drop
  const handleLeftDrag = (e: React.MouseEvent) => {
    e.stopPropagation();
    selectLayer();
    const startX = e.clientX;
    const origStart = layer.startTime;

    const handleMove = (moveEvent: MouseEvent) => {
      const dx = moveEvent.clientX - startX;
      const dt = xToTime(dx);
      const newStart = snapToFrame(Math.min(origStart + dt, layer.endTime - 1 / comp.frameRate), comp.frameRate);
      if (barRef.current) {
        barRef.current.style.left = `${timeToX(newStart)}px`;
        barRef.current.style.width = `${Math.max(timeToX(layer.endTime - newStart), 4)}px`;
      }
    };
    const handleUp = (upEvent: MouseEvent) => {
      window.removeEventListener('mousemove', handleMove);
      window.removeEventListener('mouseup', handleUp);
      const dx = upEvent.clientX - startX;
      const dt = xToTime(dx);
      const newStart = snapToFrame(Math.min(origStart + dt, layer.endTime - 1 / comp.frameRate), comp.frameRate);
      if (newStart !== origStart) {
        editor.dispatchCommand(UPDATE_PROPERTY, { layerId: layer.id, property: 'startTime', value: newStart });
      }
    };
    window.addEventListener('mousemove', handleMove);
    window.addEventListener('mouseup', handleUp);
  };

  // Right handle — commit only on drop
  const handleRightDrag = (e: React.MouseEvent) => {
    e.stopPropagation();
    selectLayer();
    const startX = e.clientX;
    const origEnd = layer.endTime;

    const handleMove = (moveEvent: MouseEvent) => {
      const dx = moveEvent.clientX - startX;
      const dt = xToTime(dx);
      const newEnd = snapToFrame(Math.max(layer.startTime + 1 / comp.frameRate, origEnd + dt), comp.frameRate);
      if (barRef.current) {
        barRef.current.style.width = `${Math.max(timeToX(newEnd - layer.startTime), 4)}px`;
      }
    };
    const handleUp = (upEvent: MouseEvent) => {
      window.removeEventListener('mousemove', handleMove);
      window.removeEventListener('mouseup', handleUp);
      const dx = upEvent.clientX - startX;
      const dt = xToTime(dx);
      const newEnd = snapToFrame(Math.max(layer.startTime + 1 / comp.frameRate, origEnd + dt), comp.frameRate);
      if (newEnd !== origEnd) {
        editor.dispatchCommand(UPDATE_PROPERTY, { layerId: layer.id, property: 'endTime', value: newEnd });
      }
    };
    window.addEventListener('mousemove', handleMove);
    window.addEventListener('mouseup', handleUp);
  };

  return (
    <div
      style={{
        position: 'relative',
        height: 28,
        width: scale.totalWidth,
        overflow: 'hidden',
      }}
    >
      <div
        ref={barRef}
        style={{
          position: 'absolute',
          left,
          width: Math.max(width, 4),
          top: 3,
          height: 22,
          background: isSelected ? color : `${color}99`,
          borderRadius: 4,
          border: isSelected ? `2px solid ${color}` : `1px solid ${color}80`,
          cursor: 'grab',
          overflow: 'hidden',
        }}
      >
        <span
          onMouseDown={handleBarDrag}
          style={{
            position: 'absolute',
            left: HANDLE_WIDTH,
            right: HANDLE_WIDTH,
            top: 0,
            bottom: 0,
            display: 'flex',
            alignItems: 'center',
            paddingLeft: 4,
            fontSize: 10,
            color: '#fff',
            whiteSpace: 'nowrap',
            fontWeight: 500,
            textShadow: '0 1px 2px rgba(0,0,0,0.3)',
            cursor: 'grab',
          }}
        >
          {layer.name}
        </span>

        <div
          onMouseDown={handleLeftDrag}
          style={{
            position: 'absolute',
            left: 0,
            top: 0,
            width: HANDLE_WIDTH,
            height: '100%',
            cursor: 'ew-resize',
            background: 'rgba(255,255,255,0.15)',
            borderRight: '1px solid rgba(255,255,255,0.3)',
          }}
        />

        <div
          onMouseDown={handleRightDrag}
          style={{
            position: 'absolute',
            right: 0,
            top: 0,
            width: HANDLE_WIDTH,
            height: '100%',
            cursor: 'ew-resize',
            background: 'rgba(255,255,255,0.15)',
            borderLeft: '1px solid rgba(255,255,255,0.3)',
          }}
        />
      </div>
    </div>
  );
}
